import React, { useEffect, useState } from 'react';
import { Truck, Users, UserCheck, Route, DollarSign, AlertTriangle, TrendingUp, TrendingDown, Wrench } from 'lucide-react';
import saasApiService from '../../services/saasApi';

interface DashboardStats {
  vehicles: number;
  drivers: number;
  clients: number;
  routes: number;
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
}

const SaasDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    vehicles: 0,
    drivers: 0,
    clients: 0,
    routes: 0,
    totalRevenue: 0,
    totalExpenses: 0,
    netProfit: 0,
  });
  const [alerts, setAlerts] = useState<any[]>([]); // Vehicle alerts
  const [trailerAlerts, setTrailerAlerts] = useState<any[]>([]);
  const [driverAlerts, setDriverAlerts] = useState<any[]>([]);
  const [maintAlerts, setMaintAlerts] = useState<any[]>([]);
  const [sendingMaintEmail, setSendingMaintEmail] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        
        // Obtener datos básicos
        const [vehiclesData, driversData, clientsData, routesData, financialData] = await Promise.all([
          saasApiService.getVehicles(),
          saasApiService.getDrivers(),
          saasApiService.getClients(),
          saasApiService.getRoutes(),
          saasApiService.getFinancialSummary(),
        ]);

        console.log('Dashboard data:', { vehiclesData, driversData, clientsData, routesData, financialData });

        // Obtener alertas de documentos
        const alertsData = await saasApiService.getVehicleAlerts();
        const trailerAlertsData = await saasApiService.getTrailerAlerts({ days: 30 }).catch(() => ({ alerts: [] }));
        const driverAlertsData = await saasApiService.getDriverAlerts({ days: 30 }).catch(() => ({ alerts: [] }));
        const maintAlertsData = await saasApiService.getMaintenanceAlerts().catch(() => ({ alerts: [] }));

        // Manejar diferentes estructuras de respuesta
        const vehicleCount = vehiclesData?.pagination?.total || vehiclesData?.length || 0;
        const driverCount = driversData?.pagination?.total || driversData?.length || 0;
        const clientCount = clientsData?.pagination?.total || clientsData?.length || 0;
        const routeCount = routesData?.pagination?.total || routesData?.length || 0;

        setStats({
          vehicles: vehicleCount,
          drivers: driverCount,
          clients: clientCount,
          routes: routeCount,
          totalRevenue: financialData?.totals?.total_revenue || financialData?.totalRevenue || 0,
          totalExpenses: financialData?.totals?.total_expenses || financialData?.totalExpenses || 0,
          netProfit: financialData?.totals?.net_profit || financialData?.netProfit || 0,
        });

        setAlerts(alertsData?.alerts || alertsData || []);
        setTrailerAlerts(trailerAlertsData?.alerts || trailerAlertsData?.data?.alerts || []);
        setDriverAlerts(driverAlertsData?.alerts || driverAlertsData?.data?.alerts || []);
        setMaintAlerts(maintAlertsData?.alerts || maintAlertsData || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const statCards = [
    {
      name: 'Vehículos',
      value: stats.vehicles,
      icon: Truck,
      color: 'bg-blue-500',
      href: '/vehicles',
    },
    {
      name: 'Conductores',
      value: stats.drivers,
      icon: UserCheck,
      color: 'bg-green-500',
      href: '/drivers',
    },
    {
      name: 'Clientes',
      value: stats.clients,
      icon: Users,
      color: 'bg-purple-500',
      href: '/clients',
    },
    {
      name: 'Rutas',
      value: stats.routes,
      icon: Route,
      color: 'bg-orange-500',
      href: '/routes',
    },
  ];

  const financialCards = [
    {
      name: 'Ingresos Totales',
      value: `S/${stats.totalRevenue.toLocaleString()}`,
      icon: TrendingUp,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      name: 'Gastos Totales',
      value: `S/${stats.totalExpenses.toLocaleString()}`,
      icon: TrendingDown,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
    },
    {
      name: 'Ganancia Neta',
      value: `S/${stats.netProfit.toLocaleString()}`,
      icon: DollarSign,
      color: stats.netProfit >= 0 ? 'text-green-600' : 'text-red-600',
      bgColor: stats.netProfit >= 0 ? 'bg-green-50' : 'bg-red-50',
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Resumen general de tu sistema de gestión de flota
        </p>
      </div>

      {/* Helpers */}
      {/* Severidad: warning (<=30 días) amarillo, urgent (<=15) rojo, expired (<=0) gris/negro */}
      {/** helper inline **/}
      {/* Alertas de vehículos */}
      {alerts.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Documentos próximos a vencer
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <ul className="list-disc pl-5 space-y-1">
                  {alerts.slice(0, 5).map((item, index) => (
                    <li key={index}>
                      {item.brand} {item.model} ({item.license_plate}) —
                      {item.alerts.map((a: any, i: number) => (
                        <span key={i} className={`ml-1 ${a.severity === 'urgent' ? 'text-red-700' : a.severity === 'expired' ? 'text-gray-800 font-semibold' : 'text-yellow-800'}`}>
                          {a.document} {a.days_remaining > 0 ? `(vence en ${a.days_remaining} días)` : '(vencido)'}
                          {i < item.alerts.length - 1 ? ',' : ''}
                        </span>
                      ))}
                    </li>
                  ))}
                </ul>
                {alerts.length > 5 && (
                  <p className="mt-1">Y {alerts.length - 5} más...</p>
                )}

      {/* Alertas de Mantenimientos */}
      {maintAlerts.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <div className="flex items-start justify-between">
            <div className="flex">
              <Wrench className="h-5 w-5 text-blue-500 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-gray-900">
                  Mantenimientos próximos o vencidos
                </h3>
                <div className="mt-2 text-sm text-gray-700">
                  <ul className="list-disc pl-5 space-y-1">
                    {maintAlerts.slice(0, 5).map((item: any, idx: number) => (
                      <li key={idx}>
                        {item.brand} {item.model} ({item.license_plate}) —{' '}
                        {Array.isArray(item.items) && item.items.map((m: any, j: number) => (
                          <span key={j} className={`ml-1 ${m.status === 'overdue' ? 'text-red-700' : m.status === 'upcoming' ? 'text-yellow-700' : 'text-gray-700'}`}>
                            {m.label} {m.status === 'overdue' ? '(vencido)' : '(próximo)'}
                            {j < item.items.length - 1 ? ',' : ''}
                          </span>
                        ))}
                      </li>
                    ))}
                  </ul>
                  {maintAlerts.length > 5 && (
                    <p className="mt-1 text-xs text-gray-600">Y {maintAlerts.length - 5} más...</p>
                  )}
                </div>
              </div>
            </div>
            <div>
              <button
                onClick={async () => {
                  try {
                    setSendingMaintEmail(true);
                    const res = await saasApiService.sendMaintenanceAlertsEmail('informes@royal-trucking.com');
                    if (res?.success && res?.sent) {
                      alert('Correo de alertas de mantenimiento enviado.');
                    } else if (res?.html_preview) {
                      const w = window.open('', '_blank');
                      if (w) {
                        w.document.write(res.html_preview);
                        w.document.close();
                      }
                      alert('No se pudo enviar el correo automáticamente. Se abrió una vista previa del contenido.');
                    } else {
                      alert('No se pudo enviar el correo de alertas.');
                    }
                  } catch (e) {
                    console.error('Error sending maintenance alert email', e);
                    alert('No se pudo enviar el correo de alertas.');
                  } finally {
                    setSendingMaintEmail(false);
                  }
                }}
                disabled={sendingMaintEmail}
                className="inline-flex items-center px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                title="Enviar alertas al correo"
              >
                {sendingMaintEmail ? 'Enviando…' : 'Enviar al correo'}
              </button>
            </div>
          </div>
        </div>
      )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alertas de remolques */}
      {trailerAlerts.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Remolques con documentos próximos a vencer (30 días): {trailerAlerts.length}
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <ul className="list-disc pl-5 space-y-1">
                  {trailerAlerts.slice(0, 5).map((a: any, idx: number) => (
                    <li key={idx}>
                      {(a.trailer_type ? a.trailer_type.charAt(0).toUpperCase() + a.trailer_type.slice(1) : 'Remolque')}
                      {a.plate ? ` (${a.plate})` : ''} —
                      {Array.isArray(a.alerts) && a.alerts.map((al:any, j:number) => (
                        <span key={j} className={`ml-1 ${al.severity === 'urgent' ? 'text-red-700' : al.severity === 'expired' ? 'text-gray-800 font-semibold' : 'text-yellow-800'}`}>
                          {al.document} {al.days_remaining > 0 ? `(vence en ${al.days_remaining} días)` : '(vencido)'}
                          {j < a.alerts.length - 1 ? ',' : ''}
                        </span>
                      ))}
                    </li>
                  ))}
                </ul>
                {trailerAlerts.length > 5 && (
                  <p className="mt-1">Y {trailerAlerts.length - 5} más...</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alertas de conductores */}
      {driverAlerts.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Conductores con documentos próximos a vencer (30 días): {driverAlerts.length}
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <ul className="list-disc pl-5 space-y-1">
                  {driverAlerts.slice(0, 5).map((d: any, idx: number) => (
                    <li key={idx}>
                      {d.name} ({d.license_number}) —
                      {Array.isArray(d.alerts) && d.alerts.map((al:any, j:number) => (
                        <span key={j} className={`ml-1 ${al.severity === 'urgent' ? 'text-red-700' : al.severity === 'expired' ? 'text-gray-800 font-semibold' : 'text-yellow-800'}`}>
                          {al.document} {al.days_remaining > 0 ? `(vence en ${al.days_remaining} días)` : '(vencido)'}
                          {j < d.alerts.length - 1 ? ',' : ''}
                        </span>
                      ))}
                    </li>
                  ))}
                </ul>
                {driverAlerts.length > 5 && (
                  <p className="mt-1">Y {driverAlerts.length - 5} más...</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card) => (
          <div
            key={card.name}
            className="relative bg-white pt-5 px-4 pb-12 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden"
          >
            <dt>
              <div className={`absolute ${card.color} rounded-md p-3`}>
                <card.icon className="h-6 w-6 text-white" />
              </div>
              <p className="ml-16 text-sm font-medium text-gray-500 truncate">
                {card.name}
              </p>
            </dt>
            <dd className="ml-16 pb-6 flex items-baseline sm:pb-7">
              <p className="text-2xl font-semibold text-gray-900">{card.value}</p>
            </dd>
          </div>
        ))}
      </div>

      {/* Financial Overview */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Resumen Financiero
          </h3>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            {financialCards.map((card) => (
              <div key={card.name} className={`${card.bgColor} rounded-lg p-4`}>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <card.icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">{card.name}</p>
                    <p className={`text-lg font-semibold ${card.color}`}>
                      {card.value}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Acciones Rápidas
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <button 
              onClick={() => window.location.href = '/dashboard/vehicles'}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Truck className="h-4 w-4 mr-2" />
              Agregar Vehículo
            </button>
            <button 
              onClick={() => window.location.href = '/dashboard/drivers'}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <UserCheck className="h-4 w-4 mr-2" />
              Agregar Conductor
            </button>
            <button 
              onClick={() => window.location.href = '/dashboard/clients'}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <Users className="h-4 w-4 mr-2" />
              Agregar Cliente
            </button>
            <button 
              onClick={() => window.location.href = '/dashboard/routes'}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
              <Route className="h-4 w-4 mr-2" />
              Nueva Ruta
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaasDashboard;
